class StickyAtcBar {
  constructor(root) {
    this.root = root;
    this.sectionId = root.dataset.sectionId;
    this.productId = root.dataset.productId;
    this.offset = Number(root.dataset.offset || 0);
    this.isVisible = false;
    this.isSyncing = false;
    this.visibilityTarget = null;

    this.refreshReferences();

    if (!this.mainProductInfo || !this.stickyProductForm) return;

    this.handleOptionValueSelectionChange = this.handleOptionValueSelectionChange.bind(this);
    this.handleVariantChange = this.handleVariantChange.bind(this);
    this.handleIntersection = this.handleIntersection.bind(this);

    this.variantChangeUnsubscriber = typeof subscribe === 'function'
      ? subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChange)
      : null;
    this.optionSelectionUnsubscriber = typeof subscribe === 'function'
      ? subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, this.handleOptionValueSelectionChange)
      : null;

    this.initializeVisibilityObserver();
    this.syncPriceFromMain();
    this.syncButtonStateFromMain();
    this.clearVariantSelectors(this.stickyVariantSelectors);
  }

  refreshReferences() {
    const previousVisibilityTarget = this.visibilityTarget;

    this.mainProductInfo =
      document.querySelector(`product-info[id^="MainProduct"][data-product-id="${this.productId}"]`) ||
      document.querySelector(`product-info[data-product-id="${this.productId}"]`);

    this.mainSectionId = this.mainProductInfo?.dataset.section;
    this.mainVariantSelectors = this.mainProductInfo?.querySelector('variant-selects');
    this.mainProductForm = this.mainProductInfo?.querySelector('product-form');

    this.stickyProductForm = this.root.querySelector('product-form');
    this.stickyVariantSelectors = this.root.querySelector('variant-selects');

    this.mainSubmitButton = this.mainProductForm?.querySelector('[type="submit"]');
    this.stickySubmitButton = this.stickyProductForm?.querySelector('[type="submit"]');
    this.stickySubmitButtonText = this.stickySubmitButton?.querySelector('span');

    const newVisibilityTarget = this.mainProductForm ? this.mainSubmitButton || this.mainProductForm : null;

    if (this.visibilityObserver && newVisibilityTarget !== previousVisibilityTarget) {
      if (previousVisibilityTarget) this.visibilityObserver.unobserve(previousVisibilityTarget);
      if (newVisibilityTarget) {
        this.visibilityObserver.observe(newVisibilityTarget);
        this.visibilityTarget = newVisibilityTarget;
      }
    } else if (this.visibilityObserver && !newVisibilityTarget && previousVisibilityTarget) {
      this.visibilityObserver.unobserve(previousVisibilityTarget);
      this.visibilityTarget = null;
    }
  }

  initializeVisibilityObserver() {
    if (!this.mainProductForm) return;

    if (!window.IntersectionObserver) {
      this.toggleVisibility(true);
      return;
    }

    this.visibilityTarget = this.mainSubmitButton || this.mainProductForm;
    this.visibilityObserver = new IntersectionObserver(this.handleIntersection, {
      root: null,
      threshold: 0,
      rootMargin: `0px 0px ${-this.offset}px 0px`,
    });

    this.visibilityObserver.observe(this.visibilityTarget);
    this.toggleVisibility(false);
  }

  handleOptionValueSelectionChange(event) {
    const target = event?.data?.target;
    if (!target || this.isSyncing) return;

    this.refreshReferences();

    if (this.mainVariantSelectors?.contains(target)) {
      this.syncSelection(target, this.stickyVariantSelectors);
    } else if (this.stickyVariantSelectors?.contains(target)) {
      this.syncSelection(target, this.mainVariantSelectors);
    }
  }

  handleIntersection(entries) {
    const entry = entries.find((item) => item.target === this.visibilityTarget);
    if (!entry) return;

    // Only show sticky ATC when user has scrolled PAST the main ATC button
    // (element is above the viewport), not when it's below the fold
    const isAboveViewport = entry.boundingClientRect.top < 0;
    const shouldShow = !entry.isIntersecting && isAboveViewport;

    this.toggleVisibility(shouldShow);
  }

  toggleVisibility(shouldShow) {
    if (this.isVisible === shouldShow) return;
    this.isVisible = shouldShow;
    this.root.classList.toggle('show', shouldShow);
    this.root.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
  }

  syncSelection(sourceInput, destinationVariantSelectors) {
    if (!destinationVariantSelectors) return;

    const optionValueId = sourceInput.dataset.optionValueId;
    if (!optionValueId) return;

    const destinationInput = destinationVariantSelectors.querySelector(`[data-option-value-id="${optionValueId}"]`);
    if (!destinationInput) return;

    const isRadio = destinationInput.tagName === 'INPUT';
    const isOption = destinationInput.tagName === 'OPTION';

    const alreadySelected = isRadio ? destinationInput.checked : destinationInput.selected;
    if (alreadySelected && !destinationInput.classList.contains('disabled')) return;

    this.isSyncing = true;

    if (isRadio) {
      destinationInput.checked = true;
      destinationInput.dispatchEvent(new Event('change', { bubbles: true }));
    } else if (isOption) {
      destinationInput.selected = true;
      destinationInput.parentElement.value = destinationInput.value;
      destinationInput.parentElement.dispatchEvent(new Event('change', { bubbles: true }));
    }

    this.isSyncing = false;
  }

  handleVariantChange(event) {
    const { sectionId, variant } = event?.data || {};
    if (!sectionId || sectionId !== this.mainSectionId) return;

    this.refreshReferences();
    this.syncPriceFromMain();
    this.syncButtonStateFromMain();
    this.updateVariantInput(variant);

    const hasSelection =
      !!this.mainVariantSelectors?.querySelector('input:checked, option[selected], option[aria-selected="true"]');

    if (!hasSelection) {
      this.clearVariantSelectors(this.stickyVariantSelectors);
    }
  }

  updateVariantInput(variant) {
    if (!this.stickyProductForm?.variantIdInput) return;

    this.stickyProductForm.variantIdInput.value = variant?.id ? variant.id : '';
    this.stickyProductForm.variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  clearVariantSelectors(selectorsRoot) {
    if (!selectorsRoot) return;

    this.isSyncing = true;

    selectorsRoot.querySelectorAll('input[type="radio"], input[type="checkbox"]').forEach((input) => {
      input.checked = false;
      input.removeAttribute('checked');
    });

    selectorsRoot.querySelectorAll('select').forEach((select) => {
      Array.from(select.options).forEach((option) => option.removeAttribute('selected'));
      select.selectedIndex = -1;
    });

    selectorsRoot.querySelectorAll('[data-selected-value]').forEach((element) => {
      if (element.classList.contains('dropdown-swatch')) {
        const swatch = element.querySelector('.swatch');
        if (swatch) {
          swatch.style.setProperty('--swatch--background', 'unset');
          swatch.classList.add('swatch--unavailable');
        }
        const label = element.querySelector('.dropdown-swatch__label');
        if (label && window.variantStrings?.selectOption) label.textContent = window.variantStrings.selectOption;
      } else if (window.variantStrings?.selectOption) {
        element.textContent = window.variantStrings.selectOption;
      }
    });

    const selectedVariantScript = selectorsRoot.querySelector('[data-selected-variant]');
    if (selectedVariantScript) selectedVariantScript.textContent = 'null';

    this.isSyncing = false;
  }

  syncPriceFromMain() {
    if (!this.mainSectionId || !this.sectionId) return;
    const mainPrice = document.getElementById(`price-${this.mainSectionId}`);
    const stickyPrice = this.root.querySelector(`#price-${this.sectionId}`);
    if (!mainPrice || !stickyPrice) return;

    stickyPrice.innerHTML = mainPrice.innerHTML;
    stickyPrice.toggleAttribute('hidden', mainPrice.hasAttribute('hidden'));

    if (mainPrice.classList.contains('hidden')) {
      stickyPrice.classList.add('hidden');
    } else {
      stickyPrice.classList.remove('hidden');
    }
  }

  syncButtonStateFromMain() {
    if (!this.mainSubmitButton || !this.stickyProductForm || !this.stickySubmitButton) return;

    this.stickySubmitButtonText = this.stickySubmitButtonText || this.stickySubmitButton.querySelector('span');

    const disabled = this.mainSubmitButton.hasAttribute('disabled');
    const mainText = this.mainSubmitButton.querySelector('span')?.textContent?.trim();

    if (disabled) {
      this.stickyProductForm.toggleSubmitButton(true, mainText || window.variantStrings?.selectOption);
    } else {
      this.stickyProductForm.toggleSubmitButton(false);
      if (mainText) this.stickySubmitButtonText.textContent = mainText;
    }

    const isLoading = this.mainSubmitButton.classList.contains('loading');
    this.stickySubmitButton.classList.toggle('loading', isLoading);

    const stickySpinner = this.stickySubmitButton.querySelector('.loading__spinner');
    const mainSpinner = this.mainSubmitButton.querySelector('.loading__spinner');
    if (stickySpinner && mainSpinner) {
      stickySpinner.classList.toggle('hidden', mainSpinner.classList.contains('hidden'));
    }
  }
}

function initializeStickyAtc() {
  const root = document.getElementById('md-sticky-atc');
  if (!root || root.dataset.initialized) return;

  root.dataset.initialized = 'true';
  new StickyAtcBar(root);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeStickyAtc);
} else {
  initializeStickyAtc();
}
